import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, ChevronLeft, ChevronRight, RotateCcw, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { VideoPlayer } from "@/components/VideoPlayer";
import { api, epLabel, fmtRuntime, fmtSize } from "@/lib/api";

const Meta = ({ label, value, testid }) => (value ? (
  <div data-testid={testid}>
    <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{label}</dt>
    <dd className="mt-1 break-words font-mono text-xs text-zinc-700">{value}</dd>
  </div>
) : null);

const EpNav = ({ v }) => (
  <div className="flex gap-2">
    {v.prev && <Link to={`/watch/${v.prev.id}`} data-testid="watch-prev-episode" className="flex h-10 items-center gap-1 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900"><ChevronLeft size={14} />Previous</Link>}
    {v.next && <Link to={`/watch/${v.next.id}`} data-testid="watch-next-episode" className="flex h-10 items-center gap-1 rounded-full bg-zinc-900 px-4 text-sm text-white hover:bg-zinc-800">Next episode<ChevronRight size={14} /></Link>}
  </div>
);

export default function Watch() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [start, setStart] = useState({ at: 0, key: 0 });
  useEffect(() => {
    setV(null);
    api.get(`/videos/${id}`).then((r) => {
      setV(r.data);
      const p = r.data.progress;
      setStart({ at: p && !p.watched ? p.position : 0, key: Date.now() });
    }).catch(() => { toast.error("Video not found"); navigate("/"); });
  }, [id, navigate]);
  if (!v) return <div className="aspect-video w-full animate-pulse rounded-xl bg-zinc-200" />;

  const watched = v.progress?.watched;
  const toggleWatched = async () => {
    await api.put(`/videos/${v.id}/watched`, { watched: !watched });
    setV({ ...v, progress: watched ? null : { position: v.duration, duration: v.duration, watched: true } });
    toast.success(watched ? "Marked as unwatched" : "Marked as watched");
  };
  const onEnded = () => {
    setV((x) => ({ ...x, progress: { ...(x.progress || {}), watched: true } }));
    if (v.next) { toast(`Up next: ${v.next.title}`); navigate(`/watch/${v.next.id}`); }
  };
  const codecs = [v.video_codec || v.probe_vcodec, v.audio_codec || v.probe_acodec].filter(Boolean).join(" / ").toUpperCase();
  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="watch-back"><ArrowLeft size={12} />Back</button>
      <VideoPlayer key={`${v.id}-${start.key}`} video={v} startAt={start.at} onEnded={onEnded} />
      <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_280px] animate-rise">
        <div>
          {v.kind === "episode" && (
            <Link to={`/show?key=${encodeURIComponent(v.show.toLowerCase())}`} className="font-mono text-xs text-amber-700 hover:underline" data-testid="watch-show-link">{v.show} · {epLabel(v)}</Link>
          )}
          <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="watch-title">{v.title}</h1>
          <p className="mt-3 flex flex-wrap gap-x-3 font-mono text-xs text-zinc-500">
            {[v.year, fmtRuntime(v.duration), v.resolution, v.content_rating, v.rating && `★ ${Number(v.rating).toFixed(1)}`].filter(Boolean).map((x) => <span key={x}>{x}</span>)}
          </p>
          {v.genres?.length > 0 && <div className="mt-4 flex flex-wrap gap-1.5">{v.genres.map((g) => <span key={g} className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs text-zinc-600">{g}</span>)}</div>}
          {v.summary && <p className="mt-6 max-w-3xl text-sm leading-relaxed text-zinc-700" data-testid="watch-summary">{v.summary}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <button onClick={() => setStart({ at: 0, key: Date.now() })} data-testid="watch-start-over" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900"><RotateCcw size={14} />Start over</button>
            <button onClick={toggleWatched} data-testid="watch-toggle-watched" className="flex h-10 items-center gap-2 rounded-full border border-zinc-300 bg-white px-4 text-sm hover:border-zinc-900">
              {watched ? <><EyeOff size={14} />Mark unwatched</> : <><Eye size={14} />Mark watched</>}
            </button>
            {watched && <span className="flex items-center gap-1 text-xs text-amber-700" data-testid="watch-watched-badge"><Check size={13} />Watched</span>}
            <span className="flex-1" />
            <EpNav v={v} />
          </div>
        </div>
        <dl className="h-fit space-y-4 rounded-lg border border-zinc-200 bg-white p-6" data-testid="watch-metadata">
          <Meta label="Source" value="Plex Media Server" testid="meta-source" />
          <Meta label="Container" value={v.container?.toUpperCase()} testid="meta-container" />
          <Meta label="Codecs" value={codecs} testid="meta-codecs" />
          <Meta label="Dimensions" value={v.width && v.height ? `${v.width} × ${v.height}` : null} testid="meta-dimensions" />
          <Meta label="File size" value={fmtSize(v.size)} testid="meta-size" />
          <Meta label="Studio" value={v.studio} testid="meta-studio" />
          <Meta label="File" value={v.filename} testid="meta-filename" />
          <Meta label="Folder" value={v.folder} testid="meta-folder" />
        </dl>
      </div>
    </div>
  );
}
